// services/APIServices/employeeService.js
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Get auth token helper
const getAuthToken = () => {
    return localStorage.getItem("authToken");
};

// Get vendorId helper
const getVendorId = () => {
    return localStorage.getItem("vendorId");
};

// ==================== FETCH ALL EMPLOYEES ====================
export const fetchEmployees = async () => {
    const token = getAuthToken();

    if (!token) throw new Error("Authentication token not found");

    try {
        const response = await axios.get(
            `${API_BASE_URL}/subscription/api/admins/get/all`,
            {
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        const data = response.data;

        // ✅ HANDLE BOTH CASES (array or single object)
        return Array.isArray(data) ? data : [data];

    } catch (error) {
        console.error("❌ Failed to fetch employees:", error.response?.data || error.message);
        throw error;
    }
};

// ==================== ADD EMPLOYEE ====================
// Add employee
// In your employeeService.js, update the addEmployee function:

// ==================== ADD EMPLOYEE ====================
export const addEmployee = async (employeeData) => {
    const token = localStorage.getItem("authToken");

    if (!token) throw new Error("Authentication token not found");

    // Validation
    if (!employeeData.name || !employeeData.phone) {
        throw new Error("Name and phone are required");
    }

    if (employeeData.phone.length < 10) {
        throw new Error("Please enter a valid mobile number");
    }

    // ✅ Correct payload as per API
    const payload = {
        id: 0,
        name: employeeData.name,
        email: employeeData.email || "",
        phoneNumber: employeeData.phone,

        username:
            employeeData.name.replace(/\s+/g, "").toLowerCase() +
            Math.floor(Math.random() * 1000),

        businessType: "TECH",              // 🔥 static or dynamic if needed
        handlingRole: employeeData.role,  // 🔥 from form

        termsAccepted: true,
        parentId: 1,                      // ✅ YOUR REQUIREMENT

        referenceId: "REF" + Date.now(),

        enabled: true,
        registerTime: new Date().toISOString(),

        businessVerticals: ["FOOD_AND_BEVERAGES"],
        businessModules: ["Home"],

        accountNonExpired: true,
        accountNonLocked: true,
        credentialsNonExpired: true
    };

    try {
        const response = await axios.post(
            `${API_BASE_URL}/subscription/api/admin/registration`,
            payload,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
            }
        );

        return response.data;

    } catch (error) {
        console.error("❌ Failed to add employee:", error.response?.data || error.message);

        if (error.response) {
            throw new Error(error.response.data?.message || "Failed to add employee");
        }

        throw error;
    }
};

// ==================== UPDATE EMPLOYEE STATUS ====================
export const updateEmployeeStatus = async (vendorId, status) => {
    const token = getAuthToken();

    if (!token) throw new Error("Authentication token not found");
    if (!vendorId) throw new Error("Employee ID not found");

    // Map status to enabled/disabled
    const enabled = status === "Active";

    try {
        ////console.log(`Updating employee ${vendorId} status to: ${status}`);

        const response = await axios.put(
            `${API_BASE_URL}/admin/update/${vendorId}`,
            { enabled },
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error("❌ Failed to update employee status:", error.response?.data || error.message);
        throw error;
    }
};

// ==================== DEACTIVATE EMPLOYEE ====================
export const deactivateEmployee = async (vendorId) => {
    return updateEmployeeStatus(vendorId, "Inactive");
};

// ==================== DELETE EMPLOYEE ====================
export const deleteEmployee = async (vendorId) => {
    const token = getAuthToken();

    if (!token) throw new Error("Authentication token not found");
    if (!vendorId) throw new Error("Employee ID not found");

    try {
        ////console.log(`Deleting employee: ${vendorId}`);

        const response = await axios.delete(
            `${API_BASE_URL}/delete/enquiry/${vendorId}`,
            {
                headers: {
                    "Accept": "*/*",
                    "Authorization": `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error("❌ Failed to delete employee:", error.response?.data || error.message);
        throw error;
    }
};

// ==================== BULK UPLOAD EMPLOYEES ====================
export const bulkUploadEmployees = async (employeesData) => {
    const results = [];
    const errors = [];

    for (const emp of employeesData) {
        try {
            const result = await addEmployee(emp);
            results.push(result);
        } catch (error) {
            errors.push({ employee: emp, error: error.message });
        }
    }

    return { success: results, errors };
};

// ==================== FORMAT EMPLOYEE FOR DISPLAY ====================
// ==================== FORMAT EMPLOYEE FOR DISPLAY ====================
// export const formatEmployeeForDisplay = (employee) => {
//     // Generate a display ID
//     const displayId = `EMP-${employee.vendorId?.toString().padStart(3, '0') || '001'}`;

//     // Determine status
//     const status = employee.enabled ? "Active" : "Inactive";

//     // Determine ID status (random for now - you might have actual field)
//     const idStatus = Math.random() > 0.3 ? "Verified" : "Pending";

//     // Format date
//     const joined = employee.createdAt ?
//         new Date(employee.createdAt).toISOString().split('T')[0] :
//         new Date().toISOString().split('T')[0];

//     return {
//         id: displayId,
//         vendorId: employee.vendorId,
//         name: employee.name || "Unknown",
//         role: employee.employeRole || "Employee",
//         department: employee.department || "General",
//         phone: employee.mobileNumber || "N/A",
//         location: employee.city || "Unknown",
//         supervisor: "Admin",
//         joined: joined,
//         portal: employee.enabled ? "Enabled" : "Disabled",
//         lastLogin: employee.lastLogin || "-",
//         status: status,
//         idStatus: idStatus,
//         email: employee.email,
//         // 🔥 CRITICAL: Store the modules from API
//         businessModules: employee.businessModules || [],
//         subModules: employee.subModules || [],
//         original: employee
//     };
// };

// ==================== FORMAT EMPLOYEE FOR DISPLAY ====================
export const formatEmployeeForDisplay = (emp) => {
    return {
        id: emp.id,
        vendorId: emp.id, // important for update APIs

        name: emp.name || "-",
        role: emp.handlingRole || "-",
        department: emp.businessType || "-",

        phone: emp.phoneNumber || "-",
        location: emp.businessVerticals?.[0] || "-",

        supervisor: emp.parentId || "Admin",

        joined: emp.registerTime
            ? new Date(emp.registerTime).toISOString().split("T")[0]
            : "-",

        status: emp.enabled ? "Active" : "Inactive",

        exitDate: emp.exitDate || "",
        remarks: emp.remarks || "",

        email: emp.email || "-"
    };
};

// ==================== UPDATE EMPLOYEE MODULES ====================
// In your employeeService.js, update the updateEmployeeModules function:

export const updateEmployeeModules = async (id, moduleData) => {
    const token = getAuthToken();

    if (!token) throw new Error("Authentication token not found");
    if (!id) throw new Error("Employee ID not found");

    try {
        const response = await axios.put(
            `${API_BASE_URL}/subscription/api/admin/update/${id}`,
            moduleData,
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error("❌ ERROR DETAILS:");
        console.error("Status:", error.response?.status);
        console.error("Response:", error.response?.data);
        throw error;
    }
};


// ==================== UPDATE EMPLOYEE DETAILS ====================
export const updateEmployee = async (vendorId, employeeData) => {
    const token = getAuthToken();

    if (!token) throw new Error("Authentication token not found");
    if (!vendorId) throw new Error("Employee ID not found");

    try {
        ////console.log(`Updating employee ${vendorId} with data:`, employeeData);

        // Build the payload based on what's being updated
        const payload = {};

        if (employeeData.role !== undefined) {
            payload.employeRole = employeeData.role;
        }

        if (employeeData.status !== undefined) {
            payload.enabled = employeeData.status === "Active";
        }

        if (employeeData.remarks !== undefined) {
            payload.remarks = employeeData.remarks;
        }

        if (employeeData.exitDate !== undefined) {
            payload.exitDate = employeeData.exitDate;
        }

        //console.log("Update payload:", payload);

        const response = await axios.put(
            `${API_BASE_URL}/edit/enquiry/${vendorId}`,
            payload,
            {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }
        );

        //console.log("✅ Employee updated successfully:", response.data);
        return response.data;
    } catch (error) {
        console.error("❌ Failed to update employee:", error.response?.data || error.message);

        // Enhanced error messages
        if (error.response) {
            const status = error.response.status;
            const data = error.response.data;

            if (status === 404) {
                throw new Error("Employee not found");
            } else if (status === 400) {
                throw new Error(data?.message || "Invalid employee data");
            } else if (status === 403) {
                throw new Error("You don't have permission to update this employee");
            } else {
                throw new Error(data?.message || "Failed to update employee");
            }
        }
        throw error;
    }
};